import { ContextProvider } from "@lit/context";
import { css, html, TemplateResult } from "lit";
import { unsafeHTML } from "lit-html/directives/unsafe-html.js";
import { customElement, property } from "lit/decorators.js";

// Lit'in type:Boolean'ı attribute varlığına bakar; "false" stringini tanımaz.
// Bu converter HTML'den show-price="false" gibi değerleri doğru çevirir.
const boolAttr = {
  fromAttribute: (v: string | null) => v !== null && v !== "false" && v !== "0",
  toAttribute: (v: boolean) => String(v),
};
import { Api } from "../api/Api.js";
import { BaseElement } from "../ui/base/BaseElement.js";
import {
  configuratorContext,
  SugarConfiguratorContext,
} from "./SugarConfiguratorContext.js";
import("./SugarConfiguratorSide");
import("./SugarConfiguratorContent");
import("./SugarConfiguratorOperations");
import("./SugarProforma.js");
import { configuratorIcons } from "../icons/ConfiguratorIcons.js";

const style = css`
  :host {
    display: block;
    height: 100vh;
    box-sizing: border-box;
  }

  .container {
    display: flex;
    flex-direction: row;
    height: 100%;
    position: relative;
    overflow: hidden;
  }

  aside {
    width: 360px;
    border-right: 1px solid #ddd;
    padding: 2px;
    overflow-y: auto;
    background-color: #fafafa;
    height: 100%;
  }

  left-side {
    width: 360px;
    height: 100%;
    border-right: 1px solid #ddd;
    padding: 2px;
    overflow: scroll;
    display: flex;
    flex-direction: column;
  }

  up-side {
    position: absolute;
    top: 0px;
    left: 50%;
    z-index: 9999;
  }

  main {
    flex: 1;
    padding: 2px;
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
  }

  .viewer-container {
    flex: 1;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }

  .placeholder {
    background: #eee;
    border: 2px dashed #ccc;
    width: 100%;
    max-width: 600px;
    height: 400px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    color: #888;
  }

  @media (max-width: 768px) {
    .container {
      flex-direction: column;
    }

    aside {
      width: 100%;
      border-right: none;
      border-bottom: 1px solid #ddd;
    }

    .viewer-container {
      padding: 16px;
    }
  }
`;

@customElement("sugar-configurator")
export class SugarConfigurator extends BaseElement {
  static styles = style;
  protected override useShadowRoot = true;

  private _configuratorId?: string;
  private _configuratorContextProvider;

  constructor() {
    super();
    this._configuratorContextProvider = new ContextProvider(this, {
      context: configuratorContext,
      initialValue: new SugarConfiguratorContext(this),
    });
  }

  @property({ attribute: "show-dimensions", converter: boolAttr })
  showDimensions = true;

  @property({ attribute: "show-clear", converter: boolAttr })
  showClear = true;

  @property({ attribute: "show-top-view", converter: boolAttr })
  showTopView = true;

  @property({ attribute: "show-product-list", converter: boolAttr })
  showProductList = true;

  @property({ attribute: "show-price", converter: boolAttr })
  showPrice = true;

  @property({ attribute: "show-create-render", converter: boolAttr })
  showCreateRender = false;

  @property({ attribute: "configurator-id", type: String })
  public set configuratorId(value: string) {
    this._configuratorId = value;
    this._configuratorContextProvider.value.configuratorId = value;

    Api.fetchConfigurator(parseInt(value)).then((data) => {
      this._configuratorContextProvider.value.model = data;
    });
  }

  public get configuratorId(): string {
    return this._configuratorId!;
  }

  protected renderHtml(): TemplateResult {
    return html /* html */ `
      <div class="container">
        <main>
          <div class="viewer-container">
            <sugar-configurator-content
              type="configurator"
              style="width:100%;height:100%"
            ></sugar-configurator-content>
          </div>

          <slot name="operations">
            <sugar-configurator-operations
              .showDimensions=${this.showDimensions}
              .showClear=${this.showClear}
              .showTopView=${this.showTopView}
              .showProductList=${this.showProductList}
              .showPrice=${this.showPrice}
              .showCreateRender=${this.showCreateRender}
            ></sugar-configurator-operations>
          </slot>
        </main>

        <aside>
          <sugar-configurator-side></sugar-configurator-side>
        </aside>
      </div>
      ${unsafeHTML(configuratorIcons)}
    `;
  }
}
